/**
 * Metric Banding and Interpretation Configuration
 * Epic 61 US61.2: Standardized banding and interpretation logic for all metrics
 * 
 * This configuration defines:
 * - Interpretation tiers (Excellent, Good, Watch/Fair, Risk/Needs Improvement)
 * - Banding thresholds for visual display
 * - Color coding for each tier
 * - Icons for visual indicators
 */

export type InterpretationTier = 'Excellent' | 'Good' | 'Fair' | 'Watch' | 'Risk' | 'Needs Improvement';

export interface InterpretationConfig {
  label: InterpretationTier;
  color: string; // Tailwind color class
  icon: string; // Emoji icon
}

export interface MetricBandingConfig {
  /** Metric key (matches entity_name in algo_run_evidence) */
  metricKey: string;
  /** Display unit for the metric */
  unit: 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS';
  /** Banding display string (e.g., "Excellent <20% • Good 20-36% • Watch 36-50% • Risk >50%") */
  bandingDisplay: string;
  /** Function to determine interpretation tier based on value */
  getInterpretation: (value: number) => InterpretationConfig;
}

// ============================================================================
// INTERPRETATION CONFIGS (reusable)
// ============================================================================

const EXCELLENT: InterpretationConfig = { label: 'Excellent', color: 'text-green-400', icon: '🟢' };
const GOOD: InterpretationConfig = { label: 'Good', color: 'text-blue-400', icon: '🔵' };
const FAIR: InterpretationConfig = { label: 'Fair', color: 'text-amber-400', icon: '🟡' };
const WATCH: InterpretationConfig = { label: 'Watch', color: 'text-amber-400', icon: '🟡' };
const RISK: InterpretationConfig = { label: 'Risk', color: 'text-red-400', icon: '🔴' };
const NEEDS_IMPROVEMENT: InterpretationConfig = { label: 'Needs Improvement', color: 'text-red-400', icon: '🔴' };

// ============================================================================
// METRIC BANDING CONFIGURATIONS
// ============================================================================

export const METRIC_BANDING: Record<string, MetricBandingConfig> = {
  // ---------------------------------------------------------------------------
  // 1. INCOME METRICS
  // ---------------------------------------------------------------------------
  'gross_income_6m': {
    metricKey: 'gross_income_6m',
    unit: 'USD',
    bandingDisplay: 'Based on income stability and consistency',
    getInterpretation: (_value: number) => {
      // Income is evaluated based on stability, not absolute value
      // This is a placeholder - actual interpretation comes from income_stability_factor
      return GOOD;
    },
  },

  // ---------------------------------------------------------------------------
  // BOTTOM 10 METRICS - Monthly Cash Flow Components
  // ---------------------------------------------------------------------------

  // Money In (Monthly) - absolute value, banding based on consistency
  'money_in_1m': {
    metricKey: 'money_in_1m',
    unit: 'USD',
    bandingDisplay: 'Monthly deposits from all income sources',
    getInterpretation: (value: number) => {
      // Absolute income amounts don't have risk tiers - just informational
      // Context depends on user's expenses and obligations
      if (value > 0) return GOOD;
      return FAIR; // Zero or negative indicates data issue
    },
  },

  // Monthly Expenses - absolute value
  'money_out_1m': {
    metricKey: 'money_out_1m',
    unit: 'USD',
    bandingDisplay: 'Monthly outflows (excluding transfers and debt payments)',
    getInterpretation: (value: number) => {
      // Expenses alone don't indicate risk - context with income matters
      if (value >= 0) return GOOD;
      return FAIR; // Negative would indicate data anomaly
    },
  },

  // Cash on Hand - absolute value, banding based on months of coverage
  'cash_on_hand': {
    metricKey: 'cash_on_hand',
    unit: 'USD',
    bandingDisplay: 'Current balances across linked depository accounts',
    getInterpretation: (value: number) => {
      // Cash on hand is informational - risk assessment uses liquid_reserves_months
      if (value >= 10000) return EXCELLENT;
      if (value >= 5000) return GOOD;
      if (value >= 1000) return FAIR;
      return WATCH;
    },
  },

  // Monthly Debt Payments - absolute value
  'monthly_debt_payments': {
    metricKey: 'monthly_debt_payments',
    unit: 'USD',
    bandingDisplay: 'Required minimum payments across all liabilities',
    getInterpretation: (value: number) => {
      // Debt payments alone don't indicate risk - DTI ratio matters more
      // Low debt payments with low income = potentially fine
      // High debt payments with high income = potentially fine
      if (value === 0) return EXCELLENT; // No debt payments
      return GOOD; // Having debt is normal
    },
  },

  // Total Outstanding Debt - absolute value, context-dependent
  'total_debt': {
    metricKey: 'total_debt',
    unit: 'USD',
    bandingDisplay: 'Total current balances across all liabilities',
    getInterpretation: (value: number) => {
      // Outstanding debt interpretation depends on income ratio (debt_payoff_months)
      // Providing basic tiers for standalone display
      if (value === 0) return EXCELLENT;
      if (value < 5000) return GOOD;
      if (value < 25000) return FAIR;
      if (value < 100000) return WATCH;
      return RISK;
    },
  },

  // Money Leftover (Net Cash Flow) - can be negative
  'money_leftover_1m': {
    metricKey: 'money_leftover_1m',
    unit: 'USD',
    bandingDisplay: 'Positive = surplus • Negative = deficit',
    getInterpretation: (value: number) => {
      // Positive leftover is good, negative is concerning
      if (value > 500) return EXCELLENT;
      if (value > 0) return GOOD;
      if (value >= -100) return WATCH; // Slight deficit
      return RISK; // Significant deficit
    },
  },

  // Net Savings (Monthly) - floored at 0
  'net_savings_1m': {
    metricKey: 'net_savings_1m',
    unit: 'USD',
    bandingDisplay: 'Savings rate: Excellent >15% • Good 10-15% • Fair 5-10% • Risk <5%',
    getInterpretation: (value: number) => {
      // Net savings is already floored at 0, so interpret as amount saved
      if (value > 1000) return EXCELLENT;
      if (value > 500) return GOOD;
      if (value > 100) return FAIR;
      if (value > 0) return WATCH;
      return RISK; // Zero savings
    },
  },

  // ---------------------------------------------------------------------------
  // 2. DEBT-TO-INCOME (DTI) RATIO
  // Source: vs-integrations/api/src/services/verascore/factors/debtToIncomeFactor.ts
  // Thresholds: <20% Excellent, 20-36% Good, 36-50% Watch, >50% Risk
  // ---------------------------------------------------------------------------
  'dti_ratio': {
    metricKey: 'dti_ratio',
    unit: 'PERCENT',
    bandingDisplay: 'Excellent <20% • Good 20-36% • Watch 36-50% • Risk >50%',
    getInterpretation: (value: number) => {
      if (value < 20) return EXCELLENT;
      if (value < 36) return GOOD;
      if (value < 50) return WATCH;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // 3. LIQUID RESERVES (in months of expenses)
  // Source: vs-integrations/api/src/services/verascore/factors/liquidReservesFactor.ts
  // Thresholds: >=12 months Excellent, 6-12 Good, 3-6 Fair, <3 Risk
  // ---------------------------------------------------------------------------
  'liquid_reserves_months': {
    metricKey: 'liquid_reserves_months',
    unit: 'MONTHS',
    bandingDisplay: 'Excellent 12+ months • Good 6-12 months • Fair 3-6 months • Risk <3 months',
    getInterpretation: (value: number) => {
      if (value >= 12) return EXCELLENT;
      if (value >= 6) return GOOD;
      if (value >= 3) return FAIR;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // 4. SPENDING RATIO (spending / income)
  // Source: vs-integrations/src/specs/scoreSpec.ts - CASH_FLOW_THRESHOLDS
  // Thresholds: <70% Excellent, 70-85% Good, 85-95% Fair, >95% Risk
  // ---------------------------------------------------------------------------
  'spending_ratio': {
    metricKey: 'spending_ratio',
    unit: 'PERCENT',
    bandingDisplay: 'Excellent <70% • Good 70-85% • Fair 85-95% • Risk >95%',
    getInterpretation: (value: number) => {
      if (value < 70) return EXCELLENT;
      if (value < 85) return GOOD;
      if (value < 95) return FAIR;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // 5. SAVINGS RATE (savings / income)
  // Source: vs-integrations/src/specs/scoreSpec.ts - SAVINGS_THRESHOLDS
  // Thresholds: >15% Excellent, 10-15% Good, 5-10% Fair, <5% Risk
  // ---------------------------------------------------------------------------
  'savings_rate': {
    metricKey: 'savings_rate',
    unit: 'PERCENT',
    bandingDisplay: 'Excellent >15% • Good 10-15% • Fair 5-10% • Risk <5%',
    getInterpretation: (value: number) => {
      if (value > 15) return EXCELLENT;
      if (value >= 10) return GOOD;
      if (value >= 5) return FAIR;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // 6. DEBT MANAGEMENT (months to payoff revolving debt)
  // Source: vs-integrations/src/specs/scoreSpec.ts - DEBT_MGMT_THRESHOLDS
  // Thresholds: <=3 months Excellent, 3-6 Good, 6-12 Fair, >12 Risk
  // ---------------------------------------------------------------------------
  'debt_payoff_months': {
    metricKey: 'debt_payoff_months',
    unit: 'MONTHS',
    bandingDisplay: 'Excellent ≤3 months • Good 3-6 months • Fair 6-12 months • Risk >12 months',
    getInterpretation: (value: number) => {
      if (value <= 3) return EXCELLENT;
      if (value <= 6) return GOOD;
      if (value <= 12) return FAIR;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // 7. PAYMENT HISTORY (late payments in last 12 months)
  // Source: vs-integrations/src/specs/scoreSpec.ts - PAYMENT_HISTORY_THRESHOLDS
  // Thresholds: 0 Excellent, 1-2 Good, 3-4 Fair, 5+ Risk
  // ---------------------------------------------------------------------------
  'late_payments_12m': {
    metricKey: 'late_payments_12m',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 0 • Good 1-2 • Fair 3-4 • Risk 5+',
    getInterpretation: (value: number) => {
      if (value === 0) return EXCELLENT;
      if (value <= 2) return GOOD;
      if (value <= 4) return FAIR;
      return RISK;
    },
  },

  // ---------------------------------------------------------------------------
  // FACTOR SCORES (0-100 scale)
  // All 6 factors use the same banding: 80+ Excellent, 60-79 Good, 40-59 Fair, <40 Needs Improvement
  // ---------------------------------------------------------------------------
  'factor_income_stability': {
    metricKey: 'factor_income_stability',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'dti_factor': {
    metricKey: 'dti_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'liquid_reserves_factor': {
    metricKey: 'liquid_reserves_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'spending_factor': {
    metricKey: 'spending_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'savings_factor': {
    metricKey: 'savings_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'debt_mgmt_factor': {
    metricKey: 'debt_mgmt_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'payment_history_factor': {
    metricKey: 'payment_history_factor',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  // ---------------------------------------------------------------------------
  // VERASCORE (0-100 scale)
  // Same banding as factors
  // ---------------------------------------------------------------------------
  'verascore_current': {
    metricKey: 'verascore_current',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'verascore_6m': {
    metricKey: 'verascore_6m',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'verascore_12m': {
    metricKey: 'verascore_12m',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },

  'verascore_24m': {
    metricKey: 'verascore_24m',
    unit: 'COUNT',
    bandingDisplay: 'Excellent 80+ • Good 60-79 • Fair 40-59 • Needs Improvement <40',
    getInterpretation: (value: number) => {
      if (value >= 80) return EXCELLENT;
      if (value >= 60) return GOOD;
      if (value >= 40) return FAIR;
      return NEEDS_IMPROVEMENT;
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get banding configuration for a metric
 * Supports fuzzy matching (e.g., "dti_ratio" matches "dti")
 *
 * FIX Jan 22, 2026: Added exact matches for Bottom 10 metrics before fuzzy matching
 */
export function getMetricBanding(metricKey: string): MetricBandingConfig | null {
  const lowerKey = metricKey?.toLowerCase() || '';

  // Exact match first (includes new Bottom 10 metrics)
  if (METRIC_BANDING[lowerKey]) {
    return METRIC_BANDING[lowerKey];
  }

  // FIX: Check specific Bottom 10 metric patterns BEFORE generic fuzzy matching
  // This prevents 'total_debt' from matching 'debt_payoff_months'
  if (lowerKey === 'money_in_1m' || lowerKey.includes('money_in')) {
    return METRIC_BANDING['money_in_1m'];
  }

  if (lowerKey === 'money_out_1m' || lowerKey.includes('money_out')) {
    return METRIC_BANDING['money_out_1m'];
  }

  if (lowerKey === 'money_leftover_1m' || lowerKey.includes('money_leftover')) {
    return METRIC_BANDING['money_leftover_1m'];
  }

  if (lowerKey === 'net_savings_1m') {
    return METRIC_BANDING['net_savings_1m'];
  }

  if (lowerKey === 'total_debt' || lowerKey === 'outstanding_debt') {
    return METRIC_BANDING['total_debt'];
  }

  if (lowerKey === 'monthly_debt_payments' || lowerKey === 'min_debt_payments') {
    return METRIC_BANDING['monthly_debt_payments'];
  }

  if (lowerKey === 'cash_on_hand') {
    return METRIC_BANDING['cash_on_hand'];
  }

  // Fuzzy match for common patterns (factors, ratios, scores)
  if (lowerKey.includes('dti') && !lowerKey.includes('factor')) {
    return METRIC_BANDING['dti_ratio'];
  }

  if (lowerKey.includes('liquid') || lowerKey.includes('reserves')) {
    if (lowerKey.includes('factor')) {
      return METRIC_BANDING['liquid_reserves_factor'];
    }
    return METRIC_BANDING['liquid_reserves_months'];
  }

  if (lowerKey.includes('spending')) {
    if (lowerKey.includes('factor')) {
      return METRIC_BANDING['spending_factor'];
    }
    return METRIC_BANDING['spending_ratio'];
  }

  if (lowerKey.includes('savings')) {
    if (lowerKey.includes('factor')) {
      return METRIC_BANDING['savings_factor'];
    }
    return METRIC_BANDING['savings_rate'];
  }

  if (lowerKey.includes('debt')) {
    if (lowerKey.includes('factor')) {
      return METRIC_BANDING['debt_mgmt_factor'];
    }
    return METRIC_BANDING['debt_payoff_months'];
  }

  if (lowerKey.includes('payment')) {
    if (lowerKey.includes('factor')) {
      return METRIC_BANDING['payment_history_factor'];
    }
    return METRIC_BANDING['late_payments_12m'];
  }

  if (lowerKey.includes('income')) {
    if (lowerKey.includes('factor') || lowerKey.includes('stability')) {
      return METRIC_BANDING['factor_income_stability'];
    }
    return METRIC_BANDING['gross_income_6m'];
  }

  if (lowerKey.includes('verascore')) {
    if (lowerKey.includes('6m')) return METRIC_BANDING['verascore_6m'];
    if (lowerKey.includes('12m')) return METRIC_BANDING['verascore_12m'];
    if (lowerKey.includes('24m')) return METRIC_BANDING['verascore_24m'];
    return METRIC_BANDING['verascore_current'];
  }

  return null;
}

/**
 * Get interpretation for a metric value
 */
export function getInterpretation(metricKey: string, value: number): InterpretationConfig | null {
  const config = getMetricBanding(metricKey);
  return config ? config.getInterpretation(value) : null;
}

/**
 * Get banding display string for a metric
 */
export function getBandingDisplay(metricKey: string): string | null {
  const config = getMetricBanding(metricKey);
  return config ? config.bandingDisplay : null;
}

